import { useState } from 'react';

import type { createRepositories } from '@/shared/api/factory';
import { useToast } from '@/shared/hooks/ui/use-toast';

import type { IMakeBooking } from '@/entities/booking/model/types';

interface UseOTPProps {
  phoneNumber: string;
  repositories: ReturnType<typeof createRepositories>;
}

interface UseOTPHook {
  otp: string;
  submitOTPLoading: boolean;
  statusOTP: OTPStatus;
  statusOTPMessage: string;
  changeOTP: (newValue: string, successCallback: (response: IMakeBooking) => Promise<void>) => void;
}

type OTPStatus = 'error' | 'success' | 'default';

export const useOTP = (props: UseOTPProps): UseOTPHook => {
  const { phoneNumber, repositories } = props;

  const { toast } = useToast();

  const [otp, setOTP] = useState<string>('');
  const [statusOTP, setStatusOTP] = useState<OTPStatus>('default');
  const [statusOTPMessage, setStatusOTPMessage] = useState<string>('');
  const [submitOTPLoading, setSubmitOTPLoading] = useState<boolean>(false);

  const changeOTP = (newValue: string, successCallback: (response: IMakeBooking) => Promise<void>) => {
    setOTP(newValue);
    submitOTP(newValue, successCallback);
  };

  const submitOTP = async (otp: string, successCallback: (response: IMakeBooking) => Promise<void>) => {
    if (otp.length === 4) {
      const payload = { code: otp, phoneNumber: phoneNumber.replace(/\D/g, '') };
      setSubmitOTPLoading(true);
      const response = await repositories.booking.confirmBooking(payload);
      setSubmitOTPLoading(false);

      setStatusOTP(response.isError ? 'error' : 'success');

      if (!response.isError && response.code === 0) {
        setStatusOTPMessage(response.message || '');

        toast({
          variant: 'success',
          title: response.message || '',
        });

        await successCallback(response);
      } else {
        toast({
          variant: 'error',
          title: response.message || '',
        });
      }
    }
  };

  return {
    otp,
    submitOTPLoading,
    statusOTP,
    statusOTPMessage,
    changeOTP,
  };
};
